/**
 * Counting down between states (PLAN §7.4: "a UI counts down locally from
 * these and re-bases on the next state"). A state's timings are computed at
 * the production's `timing.now`; the view advances them by how long it has
 * held the state — its OWN clock's elapsed time since the state arrived — so
 * a skew between the production's clock and the browser's never shows up in
 * a countdown. Only the absolute times (expected/projected end, shown as a
 * time of day) are the production's.
 */
import type { PlayoutStateWire } from '@norna/api-contract/playout.js';

type Timing = PlayoutStateWire['timing'];

/** The production's clock now, as the browser estimates it. */
export function productionNow(timing: Timing, receivedAtMs: number, clientNowMs: number): number {
  return timing.now + Math.max(0, clientNowMs - receivedAtMs);
}

/** What the clock needs to know of the on-air part beyond the timings: its segment, and whether it is timed. */
export type OnAirContext = { readonly segmentId: string; readonly untimed: boolean };

/** How much of `held` ms ran past the part's expected end (the pace only changes then). */
function overrunIn(held: number, remainingMs: number | null): number {
  return remainingMs === null ? 0 : Math.max(0, held - Math.max(0, remainingMs));
}

/**
 * The timings advanced by the time held. Ticking members move: the on-air
 * part's clock and the piece countdowns always; the running totals and the
 * on-air segment's played time only while a TIMED part is on air; over/under
 * (the show's and the segment's) only by the part of the hold that ran past
 * the on-air part's expected end — before it, the show is on pace.
 */
export function liveTiming(timing: Timing, receivedAtMs: number, clientNowMs: number, context: OnAirContext | null): Timing {
  const held = Math.max(0, clientNowMs - receivedAtMs);
  const onAir = timing.onAir;
  if (held === 0 || onAir === null) return held === 0 ? timing : { ...timing, now: timing.now + held, pieces: advancePieces(timing, held) };
  const timed = context !== null && !context.untimed;
  const over = timed ? overrunIn(held, onAir.remainingMs) : 0;
  return {
    ...timing,
    now: timing.now + held,
    playedMs: timed ? timing.playedMs + held : timing.playedMs,
    remainingMs: timed ? timing.remainingMs - held : timing.remainingMs,
    overUnderMs: timing.overUnderMs === null ? null : timing.overUnderMs + over,
    onAir: { ...onAir, elapsedMs: onAir.elapsedMs + held, remainingMs: onAir.remainingMs === null ? null : onAir.remainingMs - held },
    pieces: advancePieces(timing, held),
    segments: timing.segments.map((seg) =>
      timed && seg.segmentId === context.segmentId
        ? { ...seg, playedMs: seg.playedMs + held, remainingMs: seg.remainingMs - held, overUnderMs: seg.overUnderMs + over }
        : seg,
    ),
  };
}

function advancePieces(timing: Timing, held: number): Timing['pieces'] {
  return timing.pieces.map((p) => ({ ...p, startsInMs: p.startsInMs - held, remainingMs: p.remainingMs === null ? null : p.remainingMs - held }));
}

/**
 * When the last take happened, as the state shows it: `lastTakeTime`, or the
 * on-air instance's own take time when that is later. The two agree on a
 * production that sends every member; the in-memory gateway's deltas leave
 * `lastTakeTime` out (its `changedMembers` lists only some members), and a
 * take guard reading it alone let a second take through to a
 * `take-too-soon` nack (found in e2e).
 */
export function lastTakeOf(state: Pick<PlayoutStateWire, 'lastTakeTime' | 'current'>): number | null {
  const own = state.current?.instance.timings.take ?? null;
  if (own === null) return state.lastTakeTime;
  return state.lastTakeTime === null ? own : Math.max(own, state.lastTakeTime);
}
